// ProjectsSection.js
import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle} from 'react';
import { 
  Box, 
  Typography, 
  Dialog, 
  Button, 
  IconButton, 
  Portal,
  Menu, 
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  List
} from '@mui/material';
import { styled, useTheme, alpha } from '@mui/material/styles';
import { 
  Folder, 
  Plus, 
  X, 
  Lightbulb, 
  ChevronRight,
  ChevronDown,
  Shapes,
  MinusCircle,
  Check
} from 'lucide-react';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  deleteChat
} from '../services/AGTChatService';
import {
  getProjects,
  createProject,
  deleteProject,
  getProjectChats
} from '../../AGTHall/services/AGTHallService';
import { useProjectContext } from './ProjectContextProvider';
import { useTranslation } from 'react-i18next'; // Importar hook de traducción

// Container to constrain width
const ProjectsContainer = styled(Box)(({ theme }) => ({
  maxWidth: '235px',
  marginLeft: 'auto',
  marginRight: 'auto',
}));

// NEW: ProjectItem with updated styling to match CategoryManager
const ProjectItem = styled(Box, {
  shouldForwardProp: (prop) => !['selected', 'isAdd'].includes(prop),
})(({ theme, selected, isAdd }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: isAdd
      ? 'transparent'
      : isWallpaperMode
        ? 'rgba(255, 255, 255, 0.05)'
        : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#F2F2F7'),
    borderRadius: '28px',
    padding: '10px 16px',
    marginBottom: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: selected
      ? isWallpaperMode
        ? `2px solid #FFFFFF`
        : `2px solid ${theme.palette.mode === 'dark' ? '#0385FF' : '#007BFE'}`
      : isAdd
        ? isWallpaperMode
          ? `2px dashed rgba(255, 255, 255, 0.3)`
          : `2px dashed ${theme.palette.mode === 'dark' ? 'rgba(3, 133, 255, 0.3)' : 'rgba(0, 123, 254, 0.3)'}`
        : `2px solid transparent`,
    '&:hover': {
      backgroundColor: isAdd
        ? 'transparent'
        : isWallpaperMode
          ? 'rgba(255, 255, 255, 0.1)'
          : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#E9E9EB'),
    }
  };
});

// NEW: Folder icon styling to match CategoryIcon
const FolderIcon = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.1)'
      : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#E9E9EB'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '12px',
    flexShrink: 0,
    '& svg': {
      color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
      width: '14px',
      height: '14px',
    }
  };
});

// NEW: Project text styling to match CategoryText
const ProjectText = styled(Typography, {
  shouldForwardProp: (prop) => !['selected', 'isAdd'].includes(prop),
})(({ theme, selected, isAdd }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    flex: 1,
    fontSize: '14px',
    fontWeight: selected ? 600 : 500,
    color: isAdd
      ? '#34C759'
      : isWallpaperMode
        ? '#FFFFFF'
        : theme.palette.text.primary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };
});

// NEW: Checkmark icon to match CheckmarkIcon
const CheckmarkIcon = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: isWallpaperMode ? '#FFFFFF' : '#0385FF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {
      color: isWallpaperMode ? '#000000' : '#FFFFFF',
      width: '14px',
      height: '14px',
    }
  };
});

// Original styled components maintained
const CategoryChip = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    display: 'inline-flex',
    alignItems: 'center',
    height: '20px',
    padding: '0 8px',
    borderRadius: '10px',
    backgroundColor: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.1)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(142, 142, 147, 0.15)'
        : 'rgba(142, 142, 147, 0.1)'),
    color: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.8)'
      : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.6)'),
    fontSize: '12px',
    fontWeight: 500,
    marginLeft: '8px',
    letterSpacing: '0.2px',
    textTransform: 'capitalize',
  };
});

const ModalContent = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark' ? '#2D2D2D' : '#ffffff'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    borderRadius: '24px',
    padding: '40px',
    width: '100%',
    maxWidth: '560px',
    position: 'relative',
    minHeight: '300px',
    display: 'flex',
    flexDirection: 'column',
    color: isWallpaperMode ? '#FFFFFF' : 'inherit',
  };
});

const CloseButton = styled(IconButton)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'absolute',
    right: '16px',
    top: '16px',
    padding: '4px',
    color: isWallpaperMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary,
  };
});

const BlurBackdrop = ({ open }) => {
  const theme = useTheme();

  return (
    <Portal>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: alpha('#000000', 0.3),
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          zIndex: theme.zIndex.modal - 1,
          opacity: open ? 1 : 0,
          transition: 'opacity 300ms ease',
          visibility: open ? 'visible' : 'hidden',
        }}
      />
    </Portal>
  );
};

const StyledDialog = styled(Dialog)(({ theme }) => {
  return {
    '& .MuiDialog-paper': {
      backgroundColor: 'transparent',
      boxShadow: 'none',
      margin: '24px',
      maxWidth: '560px',
      width: '100%',
      borderRadius: '24px',
    },
    '& .MuiBackdrop-root': {
      backgroundColor: 'transparent',
    },
  };
});

const StyledInput = styled('input')(({ theme, error }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: '100%',
    padding: '16px',
    borderRadius: '12px',
    border: `1px solid ${
      error ? '#ff1744'
      : isWallpaperMode
        ? 'rgba(255, 255, 255, 0.3)'
        : (theme.palette.mode === 'dark'
          ? '#404040'
          : '#e0e0e0')
    }`,
    backgroundColor: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.1)'
      : (theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f7'),
    color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#ffffff' : '#000000'),
    fontSize: '16px',
    outline: 'none',
    transition: 'all 0.2s',
    marginTop: '16px',
    '&:focus': {
      borderColor: isWallpaperMode ? '#FFFFFF' : '#0385FF',
      boxShadow: isWallpaperMode
        ? '0 0 0 4px rgba(255, 255, 255, 0.15)'
        : '0 0 0 4px rgba(3, 133, 255, 0.15)',
      backgroundColor: isWallpaperMode
        ? 'rgba(255, 255, 255, 0.15)'
        : (theme.palette.mode === 'dark' ? '#2d2d2d' : '#ffffff'),
    },
    '&::placeholder': {
      color: isWallpaperMode
        ? 'rgba(255, 255, 255, 0.5)'
        : (theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.5)'
          : 'rgba(0, 0, 0, 0.4)'),
    },
  };
});

const InfoBox = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    backgroundColor: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.05)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(255, 191, 0, 0.08)'
        : 'rgba(255, 191, 0, 0.05)'),
    borderRadius: '16px',
    padding: '24px',
    marginTop: '32px',
    display: 'flex',
    gap: '20px',
    alignItems: 'flex-start',
    border: isWallpaperMode
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : (theme.palette.mode === 'dark'
        ? '1px solid rgba(255, 191, 0, 0.1)'
        : '1px solid rgba(0, 0, 0, 0.15)'),
    '& svg': {
      color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#ffb300' : '#ff9500'),
      width: '40px',
      height: '40px',
      marginTop: '2px',
    },
  };
});

const ActionButton = styled(Button)(({ theme, variant }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    borderRadius: '24px',
    padding: '12px 24px',
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '15px',
    transition: 'all 0.2s',
    ...(variant === 'cancel' && {
      color: isWallpaperMode
        ? '#FFFFFF'
        : (theme.palette.mode === 'dark' ? '#ff453a' : '#ff3b30'),
      backgroundColor: 'transparent',
      border: isWallpaperMode ? '1px solid rgba(255, 255, 255, 0.3)' : 'none',
      '&:hover': {
        backgroundColor: isWallpaperMode
          ? 'rgba(255, 255, 255, 0.1)'
          : (theme.palette.mode === 'dark'
            ? 'rgba(255, 69, 58, 0.1)'
            : 'rgba(255, 59, 48, 0.1)'),
      },
    }),
    ...(variant === 'contained' && {
      backgroundColor: isWallpaperMode ? '#FFFFFF' : '#0385FF',
      color: isWallpaperMode ? '#000000' : '#ffffff',
      fontWeight: 600,
      '&:hover': {
        backgroundColor: isWallpaperMode
          ? 'rgba(255, 255, 255, 0.85)'
          : '#0374e0',
      },
    }),
  };
});

const AllProjectsModalContent = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark' ? '#2D2D2D' : '#ffffff'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    borderRadius: '24px',
    padding: '24px',
    width: '100%',
    maxWidth: '560px',
    maxHeight: '80vh',
    overflow: 'auto',
    color: isWallpaperMode ? '#FFFFFF' : 'inherit',
  };
});

const AllChatsModalContent = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark' ? '#2D2D2D' : '#ffffff'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    borderRadius: '24px',
    padding: '24px',
    width: '100%',
    maxWidth: '560px',
    maxHeight: '80vh',
    overflow: 'auto',
    color: isWallpaperMode ? '#FFFFFF' : 'inherit',
  };
});

const ProjectsSection = forwardRef(({ 
  selectedCategory,
  setSelectedProject,
  selectedProject,
  user,
  onProjectChatSelect,
  onNewProject,
  onModalOpenChange,
}, ref) => {
  // Context
  const { 
    projectChats, 
    updateProjectChats,
    removeProjectChat
  } = useProjectContext();
  
  const theme = useTheme();
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const { t } = useTranslation(); // Inicializar hook de traducción

  // States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAllProjectsModalOpen, setIsAllProjectsModalOpen] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuProjectId, setMenuProjectId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  
  // Chat context menu states
  const [chatMenuAnchorEl, setChatMenuAnchorEl] = useState(null);
  const [menuChatId, setMenuChatId] = useState(null);

  // Project chats modal state
  const [openChatsModal, setOpenChatsModal] = useState(null); 
  
  // Delete project confirmation modal state
  const [confirmDeleteProjectModalOpen, setConfirmDeleteProjectModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  useEffect(() => {
    if (onModalOpenChange) {
      onModalOpenChange(isModalOpen);
    }
  }, [onModalOpenChange, isModalOpen]);
  
  // Limit visible projects based on screen size
  const visibleProjectsLimit = 3; // Changed from 5 to 3

  // Load initial projects
  useEffect(() => {
    const loadProjects = async () => {
      if (!user?.uid || !selectedCategory) return;
      
      setIsLoading(true);
      try {
        const loadedProjects = await getProjects(user.uid, selectedCategory);
        setProjects(loadedProjects);

        // Load project chats for expanded projects
        for (const projectId of expandedProjects) {
          if (!projectChats[projectId]) {
            const chatsResponse = await getProjectChats(user.uid, projectId);
            if (chatsResponse.chats) {
              // Sort from newest to oldest
              const sortedChats = [...chatsResponse.chats].sort((a, b) => {
                const dateA = new Date(a.updatedAt || a.createdAt);
                const dateB = new Date(b.updatedAt || b.createdAt);
                return dateB - dateA; // newest first
              });
              updateProjectChats(projectId, sortedChats);
            }
          }
        }
      } catch (err) {
        console.error('Error loading projects:', err);
        setSnackbarMessage(t('projectsSection.failedToLoadProjects'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadProjects();
  }, [user?.uid, selectedCategory, expandedProjects, projectChats, updateProjectChats, t]);

  // Filter and sort projects
  const filteredProjects = projects.filter(
    (project) => project.category === selectedCategory
  );
  const sortedProjects = [...filteredProjects].sort((a, b) => b.id - a.id);

  // Determine visible and hidden projects
  const visibleProjects = sortedProjects.slice(0, visibleProjectsLimit);
  const hiddenProjects = sortedProjects.slice(visibleProjectsLimit);

  // Handlers
  const handleNewProject = useCallback(() => {
    setIsModalOpen(true);
    setProjectName('');
    setError('');
    
    // Call callback if exists
    if (onNewProject) {
      onNewProject();
    }
  }, [onNewProject]);

  useImperativeHandle(ref, () => ({
    handleNewProject
  }));

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setProjectName('');
    setError('');
  };

  const handleCreateProject = async () => {
    if (!projectName.trim() || !user?.uid) {
      setError(t('projectsSection.projectNamePlaceholder'));
      return;
    }
    
    // Check for duplicates
    if (projects.some(p => 
      p.name.toLowerCase() === projectName.trim().toLowerCase() &&
      p.category === selectedCategory
    )) {
      setError('You already have a project with this name in this category. Try something unique.');
      return;
    }

    setIsLoading(true);
    try {
      const newProject = await createProject(
        user.uid,
        projectName.trim(),
        selectedCategory
      );
      
      setProjects(prev => [...prev, newProject]);
      setSelectedProject(newProject);
      setSelectedProjectId(newProject.id);
      
      setSnackbarMessage(t('projectsSection.projectCreated'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      handleCloseModal();
    } catch (err) {
      console.error('Error creating project:', err);
      setError(t('projectsSection.failedToCreateProject'));
      setSnackbarMessage(t('projectsSection.failedToCreateProject'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleProjectExpand = async (projectId) => {
    setExpandedProjects((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);

        // Load chats if not in context
        if (!projectChats[projectId]) {
          getProjectChats(user.uid, projectId)
          .then((res) => {
              const sortedChats = (res.chats || []).sort((a, b) => {
                const dateA = new Date(a.updatedAt || a.createdAt);
                const dateB = new Date(b.updatedAt || b.createdAt);
                return dateB - dateA; // newest first
              });
              updateProjectChats(projectId, sortedChats);
            })
            .catch((err) => {
              console.error('Error fetching project chats:', err);
              setSnackbarMessage(t('projectsSection.errorLoadingChats'));
              setSnackbarSeverity('error');
              setSnackbarOpen(true);
            });
        }
      }
      return newSet;
    });
  };

  const handleProjectClick = (projectId) => {
    const foundProject = projects.find((p) => p.id === projectId);
    if (!foundProject) return;
    setSelectedProject(foundProject);
    setSelectedProjectId(foundProject.id);
  };

  // Remove the unused openProjectMenu function

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuProjectId(null);
  };

  const handleChatMenuOpen = (event, projectId, chatId) => {
    event.stopPropagation();
    setChatMenuAnchorEl(event.currentTarget);
    setMenuProjectId(projectId);
    setMenuChatId(chatId);
  };

  const handleChatMenuClose = () => {
    setChatMenuAnchorEl(null);
    setMenuProjectId(null);
    setMenuChatId(null);
  };

  const handleDeleteProjectChat = async (projectId, chatId) => {
    if (!chatId || !user?.uid) return;
    
    setIsLoading(true);
    try {
      await deleteChat(user.uid, chatId);
      
      // Use context function to remove chat
      removeProjectChat(projectId, chatId);
      
      setSnackbarMessage(t('projectsSection.chatDeleted'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error deleting chat:', err);
      setSnackbarMessage(t('projectsSection.failedToDeleteChat'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
      handleChatMenuClose();
    }
  };

  // Open delete project confirmation modal
  const handleOpenDeleteProjectModal = useCallback(() => {
    handleMenuClose();
    // Get project based on menuProjectId
    const projectObj = projects.find(p => p.id === menuProjectId);
    if (!projectObj) return;

    setProjectToDelete(projectObj);
    setConfirmDeleteProjectModalOpen(true);
  }, [menuProjectId, projects]);

  // When user confirms to delete
  const handleConfirmDeleteProject = async () => {
    if (!projectToDelete || !user?.uid) {
      setConfirmDeleteProjectModalOpen(false);
      return;
    }
    
    setIsLoading(true);
    try {
      // Call API to delete project and associated chats
      await deleteProject(user.uid, projectToDelete.id);

      // Filter project from local list
      setProjects((prev) => prev.filter((proj) => proj.id !== projectToDelete.id));

      // Clear selected project if it's the one being deleted
      if (selectedProjectId === projectToDelete.id) {
        setSelectedProjectId(null);
        setSelectedProject(null);
      }

      setSnackbarMessage(t('projectsSection.projectDeleted'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error deleting project:', err);
      setSnackbarMessage(t('projectsSection.failedToDeleteProject'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
      setConfirmDeleteProjectModalOpen(false);
      setProjectToDelete(null);
    }
  };

  const handleSeeMoreClick = () => {
    setIsAllProjectsModalOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleChatClick = useCallback(
    (projectId, chatId) => {
      const foundProject = projects.find((p) => p.id === projectId);
      if (!foundProject) return;
      
      setSelectedProjectId(projectId);
      setSelectedProject(foundProject);
    
      if (onProjectChatSelect) {
        onProjectChatSelect(chatId);
      }
    },
    [projects, setSelectedProject, onProjectChatSelect]
  );

  // Open modal with all chats for a project
  const handleOpenAllChatsModal = (projectId) => {
    const chats = projectChats[projectId] || [];
    setOpenChatsModal({ projectId, chats });
  };

  // Close all chats modal
  const handleCloseAllChatsModal = () => {
    setOpenChatsModal(null);
  };

  // Render project chats with new styling
  const renderProjectChats = (project) => {
    const chats = projectChats[project.id] || [];

    // Sort from newest to oldest
    const sortedChats = [...chats].sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.createdAt);
      const dateB = new Date(b.updatedAt || b.createdAt);
      return dateB - dateA; // newest first
    });

    // Show only first 3
    const visibleChats = sortedChats.slice(0, 3);
    const remainingCount = sortedChats.length - 3;

    return (
      <>
        <List sx={{ py: 0 }}>
          {visibleChats.map((chat) => (
            <ProjectItem
              key={chat.id}
              onClick={() => handleChatClick(project.id, chat.id)}
            >
              <FolderIcon>
                <Shapes />
              </FolderIcon>
              <ProjectText>
                {chat.title || t('chat.header.newChat')}
              </ProjectText>
              
              <IconButton
                size="small"
                sx={{ padding: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChatMenuOpen(e, project.id, chat.id);
                }}
              >
                <MinusCircle size={16} color="#FF3B30" />
              </IconButton>
            </ProjectItem>
          ))}
        </List>
        {remainingCount > 0 && (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              padding: '8px 16px',
              cursor: 'pointer',
              '&:hover': { color: isWallpaperMode ? '#FFFFFF' : '#0385FF' },
              fontSize: '14px',
              color: isWallpaperMode ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary'
            }}
            onClick={() => handleOpenAllChatsModal(project.id)}
          >
            <Typography variant="body2">
              {t('projectsSection.seeMoreChats', { count: remainingCount })}
            </Typography>
          </Box>
        )}
        {chats.length === 0 && (
          <Typography
            variant="body2"
            sx={{ 
              color: 'text.secondary', 
              fontSize: '14px',
              padding: '4px 16px'
            }}
          >
            {t('projectsSection.noChats')}
          </Typography>
        )}
      </>
    );
  };

  return (
    <ProjectsContainer>
      {isLoading && projects.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box mt={1}>
          {/* Map through projects with new design */}
          {visibleProjects.map((project) => (
            <Box key={project.id}>
              {/* Project Item */}
              <ProjectItem
                selected={selectedProjectId === project.id && selectedProject}
                onClick={() => handleProjectClick(project.id)}
              >
                <FolderIcon>
                  <Folder />
                </FolderIcon>
                
                <ProjectText selected={selectedProjectId === project.id}>
                  {project.name}
                </ProjectText>
                
                {expandedProjects.has(project.id) ? (
                  <IconButton
                    size="small"
                    sx={{ 
                      width: '24px', 
                      height: '24px', 
                      p: 0, 
                      marginRight: '8px',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleProjectExpand(project.id);
                    }}
                  >
                    <ChevronDown size={16} />
                  </IconButton>
                ) : (
                  <IconButton
                    size="small"
                    sx={{ 
                      width: '24px', 
                      height: '24px', 
                      p: 0, 
                      marginRight: '8px',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleProjectExpand(project.id);
                    }}
                  >
                    <ChevronRight size={16} />
                  </IconButton>
                )}
                
                {selectedProjectId === project.id && selectedProject && (
                  <CheckmarkIcon>
                    <Check />
                  </CheckmarkIcon>
                )}
              </ProjectItem>
              
              {/* Expanded chats */}
              {expandedProjects.has(project.id) && (
                <Box sx={{ ml: 2, mt: 0.5, mb: 1 }}>
                  {renderProjectChats(project)}
                </Box>
              )}
            </Box>
          ))}
          
          {/* Add New Project button with the same style as CategoryManager */}
          <ProjectItem isAdd={true} onClick={handleNewProject}>
            <FolderIcon sx={{ 
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(52, 199, 89, 0.12)' 
                : 'rgba(52, 199, 89, 0.08)'
            }}>
              <Plus size={14} color="#34C759" />
            </FolderIcon>
            <ProjectText 
              sx={{ 
                color: '#34C759 !important', 
                fontWeight: '500 !important' 
              }}
            >
              {t('projectManager.newProject')}
            </ProjectText>
          </ProjectItem>
          
          {/* "See more" option if necessary */}
          {hiddenProjects.length > 0 && (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                padding: '8px 16px',
                cursor: 'pointer',
                '&:hover': { color: isWallpaperMode ? '#FFFFFF' : '#0385FF' },
                fontSize: '14px',
                color: isWallpaperMode ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary'
              }}
              onClick={handleSeeMoreClick}
            >
              <Typography variant="body2">
                {t('projectManager.seeMore', { count: hiddenProjects.length })}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Project menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            bgcolor: isWallpaperMode ? 'rgba(32, 32, 32, 0.9)' : 'background.paper',
            color: isWallpaperMode ? '#FFFFFF' : 'inherit',
            border: isWallpaperMode ? '1px solid rgba(255, 255, 255, 0.08)' : 'none',
            backdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
            WebkitBackdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
          }
        }}
      >
        <MenuItem
          onClick={handleOpenDeleteProjectModal}
          sx={{
            color: 'error.main',
            '&:hover': { backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.12)' : 'error.light' },
            gap: 1,
          }}
        >
          <DeleteIcon fontSize="small" />
          {t('projectsSection.deleteProject')}
        </MenuItem>
      </Menu>

      {/* Chat menu */}
      <Menu
        anchorEl={chatMenuAnchorEl}
        open={Boolean(chatMenuAnchorEl)}
        onClose={handleChatMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            bgcolor: isWallpaperMode ? 'rgba(32, 32, 32, 0.9)' : 'background.paper',
            color: isWallpaperMode ? '#FFFFFF' : 'inherit',
            border: isWallpaperMode ? '1px solid rgba(255, 255, 255, 0.08)' : 'none',
            backdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
            WebkitBackdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
          }
        }}
      >
        <MenuItem
          onClick={() => handleDeleteProjectChat(menuProjectId, menuChatId)}
          sx={{
            color: 'error.main',
            '&:hover': { backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.12)' : 'error.light' },
            gap: 1,
          }}
        >
          <DeleteIcon fontSize="small" />
          {t('projectsSection.delete')}
        </MenuItem>
      </Menu>

      {/* Create Project modal */}
      <BlurBackdrop open={isModalOpen} />
      <StyledDialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        BackdropProps={{
          invisible: true,
        }}
      >
        <ModalContent>
          <CloseButton onClick={handleCloseModal}>
            <X size={20} />
          </CloseButton>
          
          <Typography 
            variant="h6" 
            sx={{ mb: 2, fontWeight: 600, fontSize: '24px', letterSpacing: '-0.5px' }}
          >
            {t('projectsSection.projectName')}
          </Typography>
          
          <StyledInput
            placeholder={t('projectsSection.projectNamePlaceholder')}
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            error={!!error}
            disabled={isLoading}
          />
          {error && (
            <Typography color="error" sx={{ mt: 1.5, fontSize: '13px' }}>
              {error}
            </Typography>
          )}

          <InfoBox>
            <Lightbulb />
            <Box>
              <Typography
                sx={{ 
                  fontSize: '16px',
                  fontWeight: 600,
                  color: (theme) => theme.palette.mode === 'dark' ? '#ffb300' : '#ff9500',
                  letterSpacing: '-0.3px',
                }}
              >
                {t('projectsSection.infoTitle')}
              </Typography>
              <Typography
                sx={{ 
                  fontSize: '15px',
                  lineHeight: 1.5,
                  color: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.8)'
                      : 'rgba(0, 0, 0, 0.8)',
                  mt: 1,
                }}
              >
                {t('projectsSection.infoDescription')}
              </Typography>
            </Box>
          </InfoBox>

          <Box
            sx={{
              mt: 'auto',
              pt: 4,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
            <ActionButton onClick={handleCloseModal} variant="cancel" disabled={isLoading}>
              {t('projectsSection.cancel')}
            </ActionButton>
            <ActionButton onClick={handleCreateProject} variant="contained" disabled={isLoading}>
              {isLoading ? <CircularProgress size={20} color="inherit" /> : t('projectsSection.createProject')}
            </ActionButton>
          </Box>
        </ModalContent>
      </StyledDialog>

      {/* All Projects modal */}
      <BlurBackdrop open={isAllProjectsModalOpen} />
      <StyledDialog
        open={isAllProjectsModalOpen}
        onClose={() => setIsAllProjectsModalOpen(false)}
        maxWidth="sm"
        fullWidth
        BackdropProps={{
          invisible: true,
        }}
      >
        <AllProjectsModalContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {t('projectsSection.allProjects')}
              <CategoryChip>{selectedCategory}</CategoryChip>
            </Typography>
            <CloseButton onClick={() => setIsAllProjectsModalOpen(false)}>
              <X size={20} />
            </CloseButton>
          </Box>

          {sortedProjects.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4,
              }}
            >
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontSize: '14px',
                  mb: 2,
                }}
              >
                {t('projectsSection.noProjects')}
              </Typography>
              <ActionButton
                onClick={() => {
                  setIsAllProjectsModalOpen(false);
                  handleNewProject();
                }}
                variant="contained"
              >
                {t('projectsSection.createFirst')}
              </ActionButton>
            </Box>
          ) : (
            sortedProjects.map((project) => (
              <ProjectItem
                key={project.id}
                selected={selectedProjectId === project.id}
                onClick={() => {
                  setSelectedProjectId(project.id);
                  setSelectedProject(project);
                  setIsAllProjectsModalOpen(false);
                }}
              >
                <FolderIcon>
                  <Folder />
                </FolderIcon>
                <ProjectText selected={selectedProjectId === project.id}>
                  {project.name}
                </ProjectText>
                {selectedProjectId === project.id && (
                  <CheckmarkIcon>
                    <Check />
                  </CheckmarkIcon>
                )}
              </ProjectItem>
            ))
          )}
        </AllProjectsModalContent>
      </StyledDialog>

      {/* All Chats modal */}
      <BlurBackdrop open={Boolean(openChatsModal)} />
      <StyledDialog
        open={Boolean(openChatsModal)}
        onClose={handleCloseAllChatsModal}
        maxWidth="sm"
        fullWidth
        BackdropProps={{
          invisible: true,
        }}
      >
        {openChatsModal && (
          <AllChatsModalContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {t('projectChatView.chats')}
              </Typography>
              <CloseButton onClick={handleCloseAllChatsModal}>
                <X size={20} />
              </CloseButton>
            </Box>

            {openChatsModal.chats.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 4,
                }}
              >
                <Typography
                  sx={{
                    color: 'text.secondary',
                    fontSize: '14px',
                    mb: 2,
                  }}
                >
                  {t('projectsSection.noChats')}
                </Typography>
              </Box>
            ) : (
              openChatsModal.chats.map((chat) => (
                <ProjectItem
                  key={chat.id}
                  onClick={() => {
                    handleChatClick(openChatsModal.projectId, chat.id);
                    handleCloseAllChatsModal();
                  }}
                >
                  <FolderIcon>
                    <Shapes />
                  </FolderIcon>
                  <ProjectText>{chat.title || t('chat.header.newChat')}</ProjectText>
                  <IconButton
                    size="small"
                    sx={{ padding: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleChatMenuOpen(e, openChatsModal.projectId, chat.id);
                    }}
                  >
                    <MinusCircle size={16} color="#FF3B30" />
                  </IconButton>
                </ProjectItem>
              ))
            )}
          </AllChatsModalContent>
        )}
      </StyledDialog>

      {/* Delete Project confirmation modal */}
      <BlurBackdrop open={confirmDeleteProjectModalOpen} />
      <StyledDialog
        open={confirmDeleteProjectModalOpen}
        onClose={() => setConfirmDeleteProjectModalOpen(false)}
        maxWidth="sm"
        fullWidth
        BackdropProps={{
          invisible: true,
        }}
      >
        <ModalContent>
          <CloseButton onClick={() => setConfirmDeleteProjectModalOpen(false)}>
            <X size={20} />
          </CloseButton>

          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              fontSize: '24px',
              letterSpacing: '-0.5px',
            }}
          >
            {t('projectsSection.deleteProject')}
          </Typography>

          {projectToDelete ? (
            <Typography sx={{ fontSize: '15px' }}>
              {t('projectsSection.deleteConfirmation')} <strong>{projectToDelete.name}</strong>? 
              {t('projectsSection.deleteWarning')}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: '15px' }}>
              {t('projectsSection.deleteGeneric')}
            </Typography>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 'auto',
              pt: 4,
            }}
          >
            <ActionButton
              variant="cancel"
              onClick={() => setConfirmDeleteProjectModalOpen(false)}
            >
              {t('projectsSection.cancel')}
            </ActionButton>

            <ActionButton
              variant="contained"
              sx={{
                backgroundColor: '#ff3b30',
                '&:hover': {
                  backgroundColor: '#ff1e0f',
                },
              }}
              onClick={handleConfirmDeleteProject}
            >
              {t('projectsSection.delete')}
            </ActionButton>
          </Box>
        </ModalContent>
      </StyledDialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </ProjectsContainer>
  );
});

export default ProjectsSection;
